import { Component, inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { NgClass, NgTemplateOutlet } from "@angular/common";
import { Router, RouterLink } from "@angular/router";
import { NgxMapboxGLModule } from "ngx-mapbox-gl";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import * as mapboxgl from "mapbox-gl";
import { PAUtil } from "../_components/planning-assistant/classes/util";
import { GeocoderEvent } from "../_models/mapbox.interface";
import { MapBoxService } from "../_services/mapbox.service";
import { UserService, WarehouseService } from "../_services";
import { Technician } from "../_models";

@Component({
  selector: 'hawk-warehouse-create',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    RouterLink,
    NgxMapboxGLModule,
    NgTemplateOutlet,
    NgClass
  ],
  templateUrl: './warehouse-create.component.html',
  styleUrl: './warehouse-create.component.scss'
})
export class WarehouseCreateComponent implements OnInit {

  private fb = inject(FormBuilder)
  addressGeocoder?: MapboxGeocoder

  warehouseForm = this.fb.group({
    // Stammdaten
    label: this.fb.control<string>('', { validators: [Validators.required, this.labelUniqueValidator()], nonNullable: true }),
    user_id: this.fb.control<number>(null, { validators: [Validators.required] }),
    telephone: this.fb.control<string>('', Validators.pattern('[- +()0-9]+')),
    main_warehouse: this.fb.control<boolean>(false),
    freefill: this.fb.control<boolean>(false),

    // Vorlagen-Beschränkungen
    limit_template1: this.fb.control<string>(''),
    limit_template2: this.fb.control<string>(''),
    limit_template3: this.fb.control<string>(''),

    // Adresse (disabled wie gewünscht)
    company: this.fb.control<string>(''),
    firstname: this.fb.control<string>(''),
    lastname: this.fb.control<string>(''),
    street: this.fb.control<string>({ value: '', disabled: true }),
    zip: this.fb.control<string>({ value: '', disabled: true }, [
      Validators.pattern('^([a-zA-Z]{2}-)?[0-9]{2,5}( [a-zA-Z]{0,2})?$'),
    ]),
    lat: this.fb.control<number>({ value: 0, disabled: true }),
    lng: this.fb.control<number>({ value: 0, disabled: true }),
    city: this.fb.control<string>({ value: '', disabled: true }),
    country: this.fb.control<string>({ value: '', disabled: true }),
    state: this.fb.control<string>({ value: '', disabled: true }),
  });

  public technicians: Technician[] = [];
  public usedWarehouseNames: string[] = [];

  constructor(
    private mapBoxService: MapBoxService,
    private warehouseService: WarehouseService,
    private userService: UserService,
    private router: Router,
  ) {
  }

  ngOnInit(): void {
    this.resetForm()
    this.setGeocoder()
    this.loadUsers()
    this.loadUsedWarehouseNames()
  }

  resetForm():void {
    this.warehouseForm.setValue({
      label: '',
      user_id: null,
      telephone: '',
      main_warehouse: false,
      freefill: false,
      limit_template1: '',
      limit_template2: '',
      limit_template3: '',
      company: '',
      firstname: '',
      lastname: '',
      street: '',
      zip: '',
      city: '',
      country: '',
      state: '',
      lat: 0,
      lng: 0,
    });
  }

  public async setGeocoder(): Promise<void> {
    this.addressGeocoder = new MapboxGeocoder({
      accessToken: this.mapBoxService.accessToken,
      marker: false,
      language: 'de-DE',
      mapboxgl: mapboxgl,
      placeholder: 'Adresse suchen'
    })
    while (!document.getElementById('address_geocoder')) {
      await PAUtil.sleep(100)
    }
    const address_geocoder_elem = document.getElementById('address_geocoder')
    this.addressGeocoder.addTo(address_geocoder_elem);

    this.addressGeocoder.on('result', (event) => {
      this.onAddressGeocoderEvent(event)
    })
  }

  private onAddressGeocoderEvent(event: GeocoderEvent) {
    const {country, region, city, post_code, street, street_no, center, name} = PAUtil.getGeocoderEventAddress(event);

    const address_country_control = this.warehouseForm.controls['country']
    address_country_control.setValue(country)
    address_country_control.markAsTouched();
    address_country_control.updateValueAndValidity()
    const address_state_control = this.warehouseForm.controls['state']
    address_state_control.setValue(region)
    address_state_control.markAsTouched();
    address_state_control.updateValueAndValidity()
    const address_city_control = this.warehouseForm.controls['city']
    address_city_control.setValue(city)
    address_city_control.markAsTouched();
    address_city_control.updateValueAndValidity()
    const address_zip_control = this.warehouseForm.controls['zip']
    address_zip_control.setValue(post_code)
    address_zip_control.markAsTouched();
    address_zip_control.updateValueAndValidity()
    const address_street_control = this.warehouseForm.controls['street']
    address_street_control.setValue(street + ' ' + street_no)
    address_street_control.markAsTouched();
    address_street_control.updateValueAndValidity()

    const latitude_control = this.warehouseForm.controls['lat']
    latitude_control.setValue(center[1])
    latitude_control.markAsTouched();
    latitude_control.updateValueAndValidity()
    const longitude_control = this.warehouseForm.controls['lng']
    longitude_control.setValue(center[0])
    longitude_control.markAsTouched();
    longitude_control.updateValueAndValidity()

    this.warehouseForm.updateValueAndValidity()
  }

  onSubmit() {
    if (this.warehouseForm.valid) {
      this.warehouseService.createWareHouse({
        main_warehouse: this.warehouseForm.controls.main_warehouse.value,
        freefill: this.warehouseForm.controls.freefill.value,
        address_company: this.warehouseForm.controls.company.value,
        address_country: this.warehouseForm.controls.country.value,
        address_lastname: this.warehouseForm.controls.lastname.value,
        address_latitude: this.warehouseForm.controls.lat.value,
        address_longitude: this.warehouseForm.controls.lng.value,
        address_state: this.warehouseForm.controls.state.value,
        limit_template1: this.warehouseForm.controls.limit_template1.value,
        limit_template2: this.warehouseForm.controls.limit_template2.value,
        limit_template3: this.warehouseForm.controls.limit_template3.value,
        telephone: this.warehouseForm.controls.telephone.value,
        address_street: this.warehouseForm.controls.street.value,
        address_zip: this.warehouseForm.controls.zip.value,
        label: this.warehouseForm.controls.label.value,
        address_city: this.warehouseForm.controls.city.value,
        address_firstname: this.warehouseForm.controls.firstname.value,
        owner_id: this.warehouseForm.controls.user_id.value,
        uni_stock_id: '',
      }).subscribe(
        res => {
          this.router.navigate(['/lager', res.id])
        },
        error => console.log(error)
      )
    }
  }

  private loadUsers() {
    this.userService.getTechnicians().subscribe(
      res => {
        this.technicians = res.sort((a, b) => {
          return a.name < b.name ? -1 : 1
        })
      },
      error => console.log(error)
    )
  }

  private loadUsedWarehouseNames() {
    this.warehouseService.loadWarehouses().subscribe(
      res => {
        this.usedWarehouseNames = res.map(e => e.name)
      },
      error => console.log(error)
    )
  }

  private labelUniqueValidator() {
    return (control: AbstractControl) => {
      if (!control.value || !this.usedWarehouseNames?.length) {
        return null; // nichts prüfen, wenn leer oder Liste noch nicht geladen
      }

      const labelExists = this.usedWarehouseNames
        .map(name => name.trim().toLowerCase())
        .includes(control.value.trim().toLowerCase());

      return labelExists ? { labelTaken: true } : null;
    };
  }
}
